import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField, Typography,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import * as api from './paymentTermsApi';
import type { PaymentTerm, Language } from './paymentTermsApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell, mergeLocalizations } from '../common/detailView';

interface Props {
    paymentTermId: number | null;
}

export function PaymentTermView({ paymentTermId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<PaymentTerm>();
    const [languages, setLanguages] = useState<Language[]>([]);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const langs = await api.getLanguages();
            setLanguages(langs);
            if (paymentTermId === null) {
                detail.setItem({ id: 0, code: '', name: '', localizations: mergeLocalizations([], langs, buildDefaultLoc) });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getPaymentTerm(paymentTermId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [paymentTermId]);

    useEffect(() => { load(); }, [load]);

    const isCodeValid = (): boolean => !!detail.item?.code && detail.item.code.length <= 20;

    const save = async () => {
        await detail.withSaving(async () => {
            const item = detail.item!;
            const response = detail.isNew ? await api.createPaymentTerm(item) : await api.updatePaymentTerm(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = async () => {
        if (!window.confirm(t('paymentterm.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deletePaymentTerm(detail.item!.id);
            window.history.back();
        });
    };

    const updateLocalization = (languageId: number, name: string) => {
        if (!detail.item) return;
        detail.update('localizations', detail.item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    return (
        <DetailViewShell<PaymentTerm>
            title={detail.item ? `${detail.item.code} ${detail.item.name}` : ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const sortedLocs = [...item.localizations].sort((a, b) =>
                    (a.language.name ?? '').localeCompare(b.language.name ?? '')
                );
                return (
                    <Stack spacing={2}>
                        <FormRow label={t('paymentterm.code')}>
                            {!detail.isNew && !detail.isEditing ? (
                                <Typography sx={sx14}>{item.code}</Typography>
                            ) : (
                                <TextField size="small" fullWidth
                                    inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                    value={item.code}
                                    onChange={e => detail.update('code', e.target.value.toUpperCase())}
                                    error={!isCodeValid()} />
                            )}
                        </FormRow>

                        <FormRow label={t('paymentterm.name')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx14}>{item.name}</Typography>
                            ) : (
                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                    value={item.name} onChange={e => detail.update('name', e.target.value)} />
                            )}
                        </FormRow>

                        <Divider />

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                    <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('paymentterm.name')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLocs.map(loc => (
                                    <TableRow key={loc.language.id}>
                                        <TableCell sx={{ ...sx14, width: 150 }}>{loc.language.name}</TableCell>
                                        <TableCell>
                                            {!detail.isEditing ? (
                                                <Typography sx={sx14}>{loc.name}</Typography>
                                            ) : (
                                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                    value={loc.name ?? ''}
                                                    onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Stack>
                );
            }}
        </DetailViewShell>
    );
}
